const W = 960;
const H = 620;
const DURATION = 6500;

const clamp = (v, a, b) => Math.max(a, Math.min(b, v));
const ease = (t) => 1 - Math.pow(1 - t, 3.2);

function initOnionCanvas(block, source) {
	const canvas = block.querySelector('.onion-canvas');
	const art = block.querySelector('.art');
	const fallback = block.querySelector('.onion-fallback');
	const replay = block.querySelector('.onion-replay');
	const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');

	if (!canvas || !art || !Array.isArray(source) || !source.length) return;

	const ctx = canvas.getContext('2d', {alpha: true});
	if (!ctx) return;

	if (fallback) fallback.hidden = true;
	canvas.hidden = false;

	const particles = source.map((p) => ({
		x: p[0],
		y: p[1],
		r: p[2],
		dx: p[3],
		dy: p[4],
		delay: p[5] * 1000,
		duration: p[6] * 1000,
		sx: p[7],
		sy: p[8]
	}));

	let visible = true;
	let raf = 0;
	let elapsed = 0;
	let prev = performance.now();
	let mix = 0;
	let kick = 0;
	let scrollTarget = 0;
	let scrollY = window.scrollY;
	let scrollAt = performance.now();
	let step = 1;

	function request() {
		if (!raf && visible) raf = requestAnimationFrame(draw);
	}

	function resize() {
		const rect = canvas.getBoundingClientRect();
		const dpr = Math.min(window.devicePixelRatio || 1, 1.75);
		const w = Math.max(1, Math.round(rect.width * dpr));
		const h = Math.max(1, Math.round(rect.height * dpr));

		if (canvas.width !== w || canvas.height !== h) {
			canvas.width = w;
			canvas.height = h;
		}
		ctx.setTransform(w / W, 0, 0, h / H, 0, 0);
		// Density is adaptive but deterministic, avoiding visual popping on scroll.
		step = rect.width < 440 ? 3 : rect.width < 700 ? 2 : 1;
		request();
	}

	function targetScroll() {
		const rect = block.getBoundingClientRect();
		return clamp((-rect.top + 35) / Math.min(window.innerHeight * 0.72, 580), 0, 1);
	}

	function draw(t) {
		raf = 0;
		if (!visible) return;

		const dt = clamp(t - prev, 0, 50);
		prev = t;
		if (!reduced.matches) elapsed = Math.min(DURATION + 1000, elapsed + dt);
		scrollTarget = reduced.matches ? 0 : targetScroll();
		mix += (scrollTarget - mix) * (1 - Math.exp(-dt / 145));
		kick *= Math.exp(-dt / 190);

		ctx.clearRect(0, 0, W, H);
		ctx.fillStyle = '#505050';
		for (let j = 0; j < particles.length; j += step) {
			const p = particles[j];
			const phase = reduced.matches ? 1 : clamp((elapsed - p.delay) / p.duration, 0, 1);
			const progress = ease(phase);
			const spread = mix * 4.1 + kick * 1.7;
			const x = p.x + p.dx * (1 - progress) + p.sx * spread;
			const y = p.y + p.dy * (1 - progress) + p.sy * spread;

			ctx.globalAlpha = reduced.matches ? 1 : 0.25 + 0.75 * progress;
			ctx.beginPath();
			ctx.arc(x, y, p.r, 0, Math.PI * 2);
			ctx.fill();
		}
		ctx.globalAlpha = 1;

		if (
			(!reduced.matches && elapsed < DURATION + 900) ||
			Math.abs(scrollTarget - mix) > 0.002 ||
			Math.abs(kick) > 0.008
		) {
			request();
		}
	}

	function onScroll() {
		const now = performance.now();
		const dy = window.scrollY - scrollY;
		const dt = Math.max(16, now - scrollAt);

		scrollY = window.scrollY;
		scrollAt = now;
		kick = clamp(kick * 0.4 + (dy / dt) * 0.16, -0.8, 0.8);
		request();
	}

	function restart() {
		window.scrollTo({top: 0, behavior: 'instant'});
		elapsed = 0;
		mix = 0;
		kick = 0;
		prev = performance.now();
		request();
	}

	new IntersectionObserver(
		(entries) => {
			visible = entries[0].isIntersecting;
			if (visible) {
				prev = performance.now();
				request();
			} else if (raf) {
				cancelAnimationFrame(raf);
				raf = 0;
			}
		},
		{rootMargin: '100px'}
	).observe(block);

	replay?.addEventListener('click', restart);
	window.addEventListener('scroll', onScroll, {passive: true});
	new ResizeObserver(resize).observe(art);
	reduced.addEventListener?.('change', () => {
		elapsed = DURATION + 1000;
		mix = 0;
		request();
	});

	resize();
}

export default async function heroBlock(options = {}) {
	try {
		const {block} = options;

		if (!block || block.dataset.heroReady === 'true') return;
		if (!block.querySelector('.onion-canvas')) return;
		block.dataset.heroReady = 'true';

		// Loaded on demand so the particle data stays out of the main bundle.
		const {default: particles} = await import(
			/* webpackChunkName: "hero-particles" */ './particles.js'
		);

		initOnionCanvas(block, particles);
	} catch (error) {
		console.error(error);
	}
}
